import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  HelpCircle,
  ChevronRightIcon
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { runEndpoint } from '@/services/flow';
import { toast } from 'sonner';
import RecordFieldDetails from './RecordFieldDetails';

export default function RecordsScoreTable({ dataSourceId, datePath }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('record_total_act_score');
  const [sortDir, setSortDir] = useState('asc');
  const [filterBelowThreshold, setFilterBelowThreshold] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    if (dataSourceId && datePath) {
      loadRecords();
    }
  }, [dataSourceId, datePath, page, pageSize, sortBy, sortDir, filterBelowThreshold]);

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      const response = await runEndpoint('cortex.get_records_with_scores', {
        dataSourceId: parseInt(dataSourceId),
        datePath,
        page,
        pageSize,
        sortBy,
        sortDir,
        filterBelowThreshold,
        threshold: 4
      });

      if (response?.error) {
        toast.error(response.error);
        setRecords([]);
        setTotal(0);
      } else {
        setRecords(response.records || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 0);
      }
    } catch (error) {
      console.error('Error loading records:', error);
      toast.error('Failed to load records');
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const getScoreColor = (score, max) => {
    const percentage = (score / max) * 100;
    if (percentage >= 75) return 'bg-green-100 text-green-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-blue-600" />
      : <ArrowDown className="h-3 w-3 text-blue-600" />;
  };

  const SortableHeader = ({ column, children, tooltip }) => (
    <TableHead
      className="cursor-pointer hover:bg-slate-50"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                <HelpCircle className="h-3 w-3 text-slate-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <SortIcon column={column} />
      </div>
    </TableHead>
  );

  // Breadcrumb navigation
  const Breadcrumbs = () => (
    <div className="flex items-center gap-2 text-sm mb-4">
      <span
        className={`${selectedRecord ? 'text-blue-600 hover:underline cursor-pointer' : 'text-slate-900 font-medium'}`}
        onClick={() => selectedRecord && setSelectedRecord(null)}
      >
        Records with ACT Scores
      </span>
      {selectedRecord && (
        <>
          <ChevronRightIcon className="h-4 w-4 text-slate-400" />
          <span className="text-slate-900 font-medium">
            Record {selectedRecord}
          </span>
        </>
      )}
    </div>
  );

  // If a record is selected, show the field details view
  if (selectedRecord !== null) {
    return (
      <Card>
        <CardHeader>
          <Breadcrumbs />
          <CardTitle className="text-lg">Field Details for Record {selectedRecord}</CardTitle>
        </CardHeader>
        <CardContent>
          <RecordFieldDetails
            dataSourceId={dataSourceId}
            datePath={datePath}
            recordKey={selectedRecord}
          />
        </CardContent>
      </Card>
    );
  }

  // Records table view
  return (
    <Card>
      <CardHeader>
        <Breadcrumbs />
        <div className="flex items-center justify-between">
          <CardTitle>Records with ACT Scores</CardTitle>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={filterBelowThreshold}
                  onChange={(e) => {
                    setFilterBelowThreshold(e.target.checked);
                    setPage(1);
                  }}
                  className="rounded border-slate-300"
                />
                Show only below threshold
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">Filter to show only records with average ACT score per field below 4 (50% of maximum score).</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
            </div>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(parseInt(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            No records found
          </div>
        ) : (
          <>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="recordKey" tooltip="Unique identifier for each record (e.g., IMO number for ships)">Record Key</SortableHeader>
                    <SortableHeader column="record_accuracy_score" tooltip="Sum of accuracy scores for all fields. Each field scores 1 if valid, 0 if invalid.">Accuracy</SortableHeader>
                    <SortableHeader column="record_completeness_score" tooltip="Sum of completeness scores for all fields (0-3 per field based on presence and importance weight).">Completeness</SortableHeader>
                    <SortableHeader column="record_timeliness_score" tooltip="Sum of timeliness scores for all fields (0-4 per field based on data freshness and time-sensitivity weight).">Timeliness</SortableHeader>
                    <SortableHeader column="record_total_act_score" tooltip="Sum of all ACT scores (Accuracy + Completeness + Timeliness) for all fields in this record.">Total ACT</SortableHeader>
                    <SortableHeader column="field_count" tooltip="Number of fields evaluated for this record.">Fields</SortableHeader>
                    <SortableHeader column="avg_field_score" tooltip="Average ACT score per field (Total ACT ÷ Fields). Max is 8. Below 4 indicates poor quality.">Avg/Field</SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow
                      key={record.recordKey}
                      className={`cursor-pointer hover:bg-slate-100 ${record.avg_field_score < 4 ? 'bg-red-50 hover:bg-red-100' : ''}`}
                      onClick={() => setSelectedRecord(record.recordKey)}
                    >
                      <TableCell className="font-medium text-blue-600 hover:underline">
                        {record.recordKey}
                      </TableCell>
                      <TableCell>
                        <Badge className={getScoreColor(record.record_accuracy_score, record.field_count)}>
                          {record.record_accuracy_score}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getScoreColor(record.record_completeness_score, record.field_count * 3)}>
                          {record.record_completeness_score}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getScoreColor(record.record_timeliness_score, record.field_count * 4)}>
                          {record.record_timeliness_score}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getScoreColor(record.record_total_act_score, record.field_count * 8)}>
                          {record.record_total_act_score}
                        </Badge>
                      </TableCell>
                      <TableCell>{record.field_count}</TableCell>
                      <TableCell>
                        <Badge className={getScoreColor(record.avg_field_score, 8)}>
                          {record.avg_field_score?.toFixed(2)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-slate-500">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total.toLocaleString()} records
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
